import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { timezoneStore, useConfig } from "@/api/hooks/useConfig";
import AreaChart from "@/components/AreaChart";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import { Card, CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import { Granularity } from "@/components/pageCards/filterSort/periodicity";
import { FilterSort } from "@/components/pageCards/filterSort/types";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useHomeLoadingState } from "@/pages/home/loadingState";
import { useTrans } from "@/translations";

import { useCallback, useEffect, useMemo, useState } from "react";

import { capitalize } from "lodash";
import moment from "moment-timezone";
import {
  DashboardTileType,
  FilterTimeFrameValue,
} from "server/config-api/index.js";
import { TerminalPeriod } from "server/dwhApi/terminals/types.js";

type Props = {
  timeFilter?: FilterTimeFrameValue;
  setTimeFilters?: (
    value: FilterTimeFrameValue,
    type: DashboardTileType,
  ) => void;
};

export const RecyclingRateOverTimeRE: React.FC<Props> = ({
  timeFilter,
  setTimeFilters,
}) => {
  const { t, language } = useTrans();
  const { config } = useConfig();
  const { terminalConfig } = useConfigService();
  const { setRecyclingRateLoading } = useHomeLoadingState();
  const { timezone } = timezoneStore();
  const [{ period: [selectedPeriod] = [] }, setActiveOptions] = useState<
    ActiveOptions<"period">
  >({} as ActiveOptions<any>);

  const defaultPeriod = useMemo(
    () =>
      timeFilter ||
      terminalConfig?.defaultTimeFrame ||
      FilterTimeFrameValue.Last6FullMonths,
    [terminalConfig, timeFilter],
  );

  const filters: FilterSort[] = useMemo(
    () => [
      createTimeFilters({ defaultValue: defaultPeriod }, [
        FilterTimeFrameValue.Last6FullMonths,
        FilterTimeFrameValue.LastFullYear,
        FilterTimeFrameValue.SinceBeginning,
      ]),
    ],
    [defaultPeriod],
  );

  const periodFilterValue = useMemo(
    () =>
      getTimeFrame(
        selectedPeriod?.value as FilterTimeFrameValue,
        defaultPeriod,
      ),
    [selectedPeriod, defaultPeriod],
  );

  const { currentTerminal } = useTerminalsState();

  const {
    data: periodData,
    isError: isPeriodError,
    isLoading: isPeriodLoading,
  } = trpc.dwh.terminals.getPeriods.useQuery(
    {
      ...periodFilterValue,
      timeZone: timezone,
      terminalId: currentTerminal?.id || "",
      granularity: Granularity.MONTH,
    },
    {
      queryKeyHashFn: () =>
        [
          "terminalPeriods",
          periodFilterValue.startTimeISO,
          periodFilterValue.endTimeISO,
          timezone,
          currentTerminal?.id,
        ].toString(),
      enabled: !!currentTerminal,
    },
  );

  const currentMonthTimeValue = useMemo(
    () => ({
      startTimeISO: moment().tz(timezone, true).startOf("month").toISOString(),
      endTimeISO: moment().tz(timezone, true).endOf("day").toISOString(),
    }),
    [timezone],
  );

  const {
    data: currentMonthData,
    isLoading: isCurrentMonthLoading,
    isError: isCurrentMonthError,
  } = trpc.dwh.terminals.getById.useQuery(
    {
      startTimeISO: currentMonthTimeValue.startTimeISO,
      endTimeISO: currentMonthTimeValue.endTimeISO,
      terminalId: currentTerminal?.id || "",
    },
    {
      queryKeyHashFn: () =>
        [
          "terminal",
          currentTerminal?.id,
          currentMonthTimeValue.startTimeISO,
          currentMonthTimeValue.endTimeISO,
        ].toString(),
      enabled: !!currentTerminal,
    },
  );

  useEffect(() => {
    setRecyclingRateLoading(isPeriodLoading || isCurrentMonthLoading);
  }, [setRecyclingRateLoading, isPeriodLoading, isCurrentMonthLoading]);

  const labelFormat = useMemo(
    () =>
      selectedPeriod?.value === FilterTimeFrameValue.SinceBeginning
        ? "MMM YYYY"
        : "MMM",
    [selectedPeriod],
  );

  const getPeriodValue = useCallback(
    (startTime: string) =>
      capitalize(
        moment(startTime).tz(timezone).locale(language).format(labelFormat),
      ),
    [language, timezone, labelFormat],
  );

  const valueKey = useMemo(() => t("statisticsLabels:recyclingRate"), [t]);

  const recyData = useMemo(() => {
    const olderMonthsData =
      periodData?.periods.map((p: TerminalPeriod) => {
        const period = getPeriodValue(p.startTime);
        return {
          name: period,
          [valueKey]: p.terminal.sortingDegree * 100,
        };
      }) || [];

    const data = olderMonthsData;
    if (currentMonthData) {
      const { sortingDegree, totalOccurrences } = currentMonthData.terminal;

      if (totalOccurrences > 0) {
        const maxNrOfMonths =
          selectedPeriod?.value === FilterTimeFrameValue.LastFullYear ? 12 : 6;
        if (data.length === maxNrOfMonths) {
          data.shift();
        }

        data.push({
          name: capitalize(
            moment().tz(timezone, true).locale(language).format(labelFormat),
          ),
          [valueKey]: sortingDegree * 100,
        });
      }
    }

    return data;
  }, [
    periodData,
    currentMonthData,
    getPeriodValue,
    selectedPeriod,
    timezone,
    language,
    valueKey,
    labelFormat,
  ]);

  const chart = useMemo(() => {
    if (isPeriodLoading || isCurrentMonthLoading) {
      return (
        <div className="flex h-full min-h-full items-center justify-center">
          <LoadingIndicator />
        </div>
      );
    }

    if (isPeriodError || isCurrentMonthError) {
      return <EmptyView type="failToFetch" className="min-h-[300px]" />;
    }

    if (!periodData?.metadata.eventsExist) {
      return <EmptyView type="noData" className="min-h-[300px]" />;
    }

    const isEmpty = !recyData.find((d) => Number(d[valueKey]) > 0);
    if (isEmpty) {
      return (
        <EmptyView
          type="noDataCalculationPeriodUncomplete"
          className="min-h-[300px]"
        />
      );
    }

    return (
      <AreaChart
        data={recyData}
        color={
          terminalConfig?.primaryColor || config?.primaryColor || "#72723B"
        }
        dataKey={valueKey}
      />
    );
  }, [
    recyData,
    isPeriodError,
    isCurrentMonthError,
    isPeriodLoading,
    isCurrentMonthLoading,
    periodData,
    terminalConfig,
    config,
    valueKey,
  ]);

  const onFilterOptionChange = useCallback(
    (activeOptions: ActiveOptions<any>) => {
      setActiveOptions(activeOptions);

      if (setTimeFilters && activeOptions.period) {
        setTimeFilters(
          activeOptions.period[0].value as FilterTimeFrameValue,
          DashboardTileType.RecyclingRateOverTime,
        );
      }
    },
    [setActiveOptions, setTimeFilters],
  );

  return (
    <Card
      title={"circularityLabels:recyclingRateOverTime"}
      filters={filters}
      onFilterOptionChange={onFilterOptionChange}
      filterLocation={CardComponentFilterLocation.RIGHT}
    >
      {chart}
    </Card>
  );
};
